import React from 'react';
import type {
  DividendCellAlign,
  DividendMatrixTableData,
  DividendTableCell,
  DividendTableRow,
} from '@app-types/performanceDividend';
import './DividendMatrixTable.less';

interface DividendMatrixTableProps {
  data: DividendMatrixTableData;
  onToggleRow?: (row: DividendTableRow) => void;
}

const DividendMatrixTable: React.FC<DividendMatrixTableProps> = ({ data, onToggleRow }) => {
  const { columns, rows, minWidth, mobileMinWidth, labelColumnWidth, nodeId, caption } = data;

  const tableStyle = {
    '--dividend-table-min-width': `${minWidth ?? 0}px`,
    '--dividend-table-mobile-min-width': `${mobileMinWidth ?? minWidth ?? 0}px`,
    ...(labelColumnWidth ? { '--dividend-label-col-width': `${labelColumnWidth}px` } : {}),
  } as React.CSSProperties;

  const alignClass = (align: DividendCellAlign | undefined, fallback: DividendCellAlign) =>
    `dividend-matrix__cell--${align ?? fallback}`;

  const renderLines = (lines: string[]) =>
    lines.map((line, index) => (
      <span key={`${line}-${index}`} className="dividend-matrix__line">
        {line}
      </span>
    ));

  const renderDataCell = (cell: DividendTableCell, index: number) => (
    <td
      key={index}
      className={`dividend-matrix__cell dividend-matrix__cell--data ${alignClass(
        cell.align ?? columns[index + 1]?.align,
        'right',
      )}${cell.nowrap === false ? '' : ' dividend-matrix__cell--nowrap'}`}
    >
      <span className="dividend-matrix__line">{cell.text}</span>
    </td>
  );

  return (
    <div className="dividend-table-wrap">
      <table className="dividend-matrix-table" style={tableStyle} data-node-id={nodeId}>
        <caption className="dividend-matrix__caption">{caption}</caption>
        <thead>
          <tr>
            {columns.map((col, index) => (
              <th
                key={col.key}
                scope="col"
                className={`dividend-matrix__cell dividend-matrix__cell--header ${alignClass(
                  col.align,
                  index === 0 ? 'left' : 'right',
                )}${col.nowrap === false ? '' : ' dividend-matrix__cell--nowrap'}`}
                style={
                  index === 0 && labelColumnWidth
                    ? { width: labelColumnWidth }
                    : col.width
                      ? { width: col.width }
                      : undefined
                }
              >
                {renderLines(col.label)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} data-node-id={row.nodeId}>
              <th
                scope="row"
                className={`dividend-matrix__cell dividend-matrix__cell--row-label dividend-matrix__cell--left${
                  row.variant === 'header-like'
                    ? ' dividend-matrix__cell--header'
                    : ' dividend-matrix__cell--data'
                }`}
                style={labelColumnWidth ? { width: labelColumnWidth } : undefined}
              >
                <span className="dividend-matrix__row-label">
                  <span className="dividend-matrix__line">{row.label}</span>
                  {row.disclosure && (
                    <button
                      type="button"
                      className="dividend-matrix__disclosure"
                      aria-label={row.disclosure === 'down' ? '明細を表示' : '明細を非表示'}
                      aria-expanded={row.disclosure === 'up'}
                      onClick={() => onToggleRow?.(row)}
                    >
                      <span
                        className={`dividend-matrix__arrow dividend-matrix__arrow--${row.disclosure}`}
                        aria-hidden="true"
                      />
                    </button>
                  )}
                </span>
              </th>
              {row.cells.map(renderDataCell)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DividendMatrixTable;
